import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import styles from './ThinkingBlock.module.scss';
import type { ThinkingTimelineRound } from '../chatBox/sseEvent';
import { useTypewriterText } from './useTypewriterText';

interface ThinkingBlockProps {
  content?: string;
  thinkingTime?: number; // 思考用时（秒）
  replying?: boolean; // 是否正在回复
  rounds?: ThinkingTimelineRound[];
  onTyping?: () => void;
  enableTypewriter?: boolean;
  forceCompleteTypewriter?: boolean;
}

interface ThinkingRoundItemProps {
  round: ThinkingTimelineRound;
  index: number;
  total: number;
  collapsed: boolean;
  onToggle: (id: string) => void;
  onTyping?: () => void;
  enableTypewriter?: boolean;
  forceCompleteTypewriter?: boolean;
}

const ThinkingRoundItem: React.FC<ThinkingRoundItemProps> = ({
  round,
  index,
  total,
  collapsed,
  onToggle,
  onTyping,
  enableTypewriter = false,
  forceCompleteTypewriter = false,
}) => {
  const displayContent = useTypewriterText(round.content, {
    active: enableTypewriter && round.active,
    minStep: 1,
    maxStep: 6,
    onTyping,
    forceComplete: forceCompleteTypewriter || (enableTypewriter && !round.active),
  });

  return (
    <div className={styles.round}>
      <span className={`${styles.dot} ${round.active ? styles.activeDot : ''}`} />
      {index < total - 1 && <span className={styles.line} />}
      <button type="button" className={styles.roundHeader} onClick={() => onToggle(round.id)}>
        <span className={styles.roundTitle}>{round.title}</span>
        {round.thinkingTime !== undefined && <span className={styles.timeLabel}>（用时{round.thinkingTime}s）</span>}
        <ChevronDownIcon className={`${styles.roundChevron} ${!collapsed ? styles.chevronExpanded : ''}`} />
      </button>
      <div
        className={`${styles.roundCollapse} ${!collapsed ? styles.collapseExpanded : ''}`}
        aria-hidden={collapsed}
      >
        <div className={styles.collapseInner}>
          <div className={styles.roundContent}>
            <div className={styles.contentText}>{displayContent}</div>
            {round.toolStatuses.length > 0 && (
              <div className={styles.tools}>
                {round.toolStatuses.map((status, toolIndex) => (
                  <span className={styles.tool} key={`${status}-${toolIndex}`}>
                    {status}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * ThinkingBlock 展示组件
 * 类似 DeepSeek 的思考过程展示，支持展开/收起
 */
const ThinkingBlock: React.FC<ThinkingBlockProps> = ({
  content = '',
  thinkingTime,
  replying = false,
  rounds,
  onTyping,
  enableTypewriter = false,
  forceCompleteTypewriter = false,
}) => {
  const normalizedRounds = useMemo<ThinkingTimelineRound[]>(
    () =>
      rounds?.length
        ? rounds
        : [
            {
              id: 'legacy-thinking-round',
              title: '思考过程',
              content,
              toolStatuses: [],
              thinkingTime,
              active: replying,
            },
          ],
    [content, replying, rounds, thinkingTime],
  );
  const [expanded, setExpanded] = useState(replying);
  const [collapsedRounds, setCollapsedRounds] = useState<Set<string>>(new Set());

  useEffect(() => {
    setExpanded(replying);
  }, [replying, normalizedRounds.length]);

  const totalTime = normalizedRounds.reduce((total, round) => total + (round.thinkingTime ?? 0), 0);
  const hasKnownTime = normalizedRounds.every(round => round.thinkingTime !== undefined);
  const toggleRound = (id: string) => {
    setCollapsedRounds(current => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className={styles.thinkingBlock}>
      <button type="button" className={styles.header} onClick={() => setExpanded(current => !current)}>
        <span className={styles.title}>{replying ? '正在思考' : '思考过程'}</span>
        <span className={styles.summary}>
          （共{normalizedRounds.length}轮{hasKnownTime ? `，共用时${totalTime}s` : ''}）
        </span>
        <ChevronDownIcon className={`${styles.chevron} ${expanded ? styles.chevronExpanded : ''}`} />
      </button>

      <div
        className={`${styles.timelineCollapse} ${expanded ? styles.collapseExpanded : ''}`}
        aria-hidden={!expanded}
      >
        <div className={styles.collapseInner}>
          <div className={styles.timeline}>
            {normalizedRounds.map((round, index) => (
              <ThinkingRoundItem
                key={round.id}
                round={round}
                index={index}
                total={normalizedRounds.length}
                collapsed={collapsedRounds.has(round.id)}
                onToggle={toggleRound}
                onTyping={onTyping}
                enableTypewriter={enableTypewriter}
                forceCompleteTypewriter={forceCompleteTypewriter}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ThinkingBlock);
